import {AuthTokenProvider} from 'api/AuthTokenProvider';
import {BaseApi} from 'api/BaseApi';
import {
    CartOrderSuccessInfoParams,
    CartOrderSuccessInfoResponse,
    CompanyPartnersResponse,
    CreateLocationsParams,
    CreateLocationsResponse,
    DeleteLocationsParams,
    DeleteLocationsResponse,
    DeleteOrderParams,
    DeleteOrderResponse,
    FeedbackResponse,
    ForwardDocumentParams,
    ForwardDocumentResponse,
    GetCartCompaniesResponse,
    GetCurrentDiscountResponse,
    GetCurrentUserResponse,
    GetFavoriteProductsResponse,
    GetLegalsParams,
    GetLegalsResponse,
    GetLocationsResponse,
    GetOrderDetailsParams,
    GetOrderDetailsResponse,
    GetOrdersParams,
    GetOrdersResponse,
    GetPickupLocationsParams,
    GetPickupLocationsResponse,
    GetWarehouseProductsParams,
    GetWarehouseProductsResponse,
    GetWarehouseRootCategoriesResponse,
    GetWarehouseSubcategoriesParams,
    GetWarehouseSubcategoriesResponse,
    OrderComplaintParams,
    OrderComplaintResponse,
    SendSmsParams,
    SendSmsResponse,
    SignInIndividualParams,
    SignInIndividualResponse,
    SignInLegalParams,
    SignInLegalResponse,
    SignUpIndividualParams,
    SignUpIndividualResponse,
    SignUpLegalParams,
    SignUpLegalResponse,
    SummaryInfoResponse,
    UpdateLocationsParams,
    UpdateLocationsResponse,
    UpdateUserParams,
    UpdateUserResponse,
    UploadFilesParams,
    UploadFilesResponse,
    VerifyAuthParams,
    VerifyAuthResponse,
    VerifyPhoneParams,
    VerifyPhoneResponse,
    VerifySmsCodeParams,
    VerifySmsCodeResponse,
} from 'api/dataTypes';
import {AxiosError} from 'axios';

import {
    getMockGetWarehouseProductsResponse,
    getMockGetWarehouseSubcategoriesResponse,
    mockCompanyPartners,
    mockGetWarehouseRootCategoriesResponse,
} from './mocks';

const LK_API_BASE_PATH = 'lk-api';
const LK_DEALER_API_BASE_PATH = 'lk-dealer-api';

export class LkApi extends BaseApi {
    private mockIsEnabled: boolean =
        process.env.NODE_ENV === 'development' ? false : false;
    constructor(type: 'retail' | 'dealers') {
        const baseURLs = {
            retail: `https://www.klenmarket.ru/${LK_API_BASE_PATH}`,
            dealers: `https://www.klenmarket.ru/${LK_DEALER_API_BASE_PATH}`,
        };

        super({baseURL: baseURLs[type]});

        this.setup();
    }

    signInLegal = (params: SignInLegalParams): Promise<SignInLegalResponse> => {
        return this.httpClient.post('/legal-authorization/', {
            verification_token: params.verificationToken,
            inn: params.inn,
        });
    };

    signInDealer = (
        params: SignInLegalParams,
    ): Promise<SignInLegalResponse> => {
        return this.httpClient.post('/dealer-authorization/', {
            verification_token: params.verificationToken,
            inn: params.inn,
        });
    };

    signInIndividual = (
        params: SignInIndividualParams,
    ): Promise<SignInIndividualResponse> => {
        return this.httpClient.post('/individual-authorization/', {
            verification_token: params.verificationToken,
        });
    };

    signUpLegal = (params: SignUpLegalParams): Promise<SignUpLegalResponse> => {
        return this.httpClient.post('/legal-registration/', {
            name: params.name,
            last_name: params.lastName,
            middle_name: params.middleName,
            email: params.email,
            inn: params.inn,
            verification_token: params.verificationToken,
        });
    };

    signUpIndividual = (
        params: SignUpIndividualParams,
    ): Promise<SignUpIndividualResponse> => {
        return this.httpClient.post('/individual-registration/', {
            name: params.name,
            last_name: params.lastName,
            middle_name: params.middleName,
            email: params.email,
            verification_token: params.verificationToken,
        });
    };

    sendSms = (params: SendSmsParams): Promise<SendSmsResponse> => {
        return this.httpClient.post('/sms-sending/', {
            phone: params.phone,
            send_token: params.sendToken,
        });
    };

    verifySmsCode = (
        params: VerifySmsCodeParams,
    ): Promise<VerifySmsCodeResponse> => {
        return this.httpClient.post('/sms-verification/', {
            send_token: params.sendToken,
            number: params.code,
        });
    };

    verifyAuth = (params: VerifyAuthParams): Promise<VerifyAuthResponse> => {
        return this.httpClient.post('/token-verification/', {
            access_token: params.accessToken,
        });
    };

    verifyPhone = (params: VerifyPhoneParams): Promise<VerifyPhoneResponse> => {
        return this.httpClient.post('/phone-verification/', {
            phone: params.phone,
        });
    };

    getLegals = (params: GetLegalsParams): Promise<GetLegalsResponse> => {
        return this.httpClient.get(`/list-inn/${params.inn}/`);
    };

    logout = (): void => {
        this.httpClient.defaults.headers.common.Authorization = '';
        AuthTokenProvider.removeToken();
    };

    getCartOrderSuccessInfo = (
        params: CartOrderSuccessInfoParams,
    ): Promise<CartOrderSuccessInfoResponse> => {
        return this.httpClient.get(`/orders/completed/${params.orderId}/`);
    };

    companyPartners = (): Promise<CompanyPartnersResponse> => {
        if (this.mockIsEnabled) {
            return Promise.resolve(mockCompanyPartners);
        }

        return this.httpClient.get('/legal-members/');
    };

    updateUser = (params: UpdateUserParams): Promise<UpdateUserResponse> => {
        return this.httpClient.put('/identification', {
            name: params.name,
            email: params.email,
            phone: params.phone,
            surname: params.surname,
            verification_token: params.verificationToken,
        });
    };

    getCartCompanies = (): Promise<GetCartCompaniesResponse> => {
        return this.httpClient.get('/counterparties/', {});
    };

    getCurrentDiscount = (): Promise<GetCurrentDiscountResponse> => {
        return this.httpClient.get(`/sales/`);
    };

    getFavoriteProducts = (): Promise<GetFavoriteProductsResponse> => {
        return this.httpClient.get(`/sales/`);
    };

    getLocations = (): Promise<GetLocationsResponse> => {
        return this.httpClient.get('/addresses/');
    };

    updateLocation = (
        params: UpdateLocationsParams,
    ): Promise<UpdateLocationsResponse> => {
        return this.httpClient.put(`/addresses/${params.id}/`, {
            latitude: params.latitude,
            longitude: params.longitude,
            address: params.address,
        });
    };

    createLocation = (
        params: CreateLocationsParams,
    ): Promise<CreateLocationsResponse> => {
        return this.httpClient.post('/addresses/', {
            latitude: params.latitude,
            longitude: params.longitude,
            address: params.address,
        });
    };

    deleteLocation = (
        params: DeleteLocationsParams,
    ): Promise<DeleteLocationsResponse> => {
        return this.httpClient.delete(`/addresses/${params.id}/`);
    };

    getPickupLocations = (
        params: GetPickupLocationsParams,
    ): Promise<GetPickupLocationsResponse> => {
        return this.httpClient.get(`/addresses-pickup/${params.region}/`);
    };

    getOrders = (params: GetOrdersParams): Promise<GetOrdersResponse> => {
        if (params.type === 'my') {
            return this.httpClient.get('/orders/');
        } else {
            return this.httpClient.get('/orders/counterparties/');
        }
    };

    getOrderDetails = (
        params: GetOrderDetailsParams,
    ): Promise<GetOrderDetailsResponse> => {
        if (params.type === 'counterparties') {
            return this.httpClient.get(`/orders/counterparties/${params.id}/`);
        } else {
            return this.httpClient.get(`/orders/${params.id}/`);
        }
    };

    deleteOrder = (params: DeleteOrderParams): Promise<DeleteOrderResponse> => {
        return this.httpClient.delete(`/orders/${params.id}/`);
    };

    complaintOrder = (
        params: OrderComplaintParams,
    ): Promise<OrderComplaintResponse> => {
        return this.httpClient.post(`/product-complaint/`, {
            orderId: params.id,
        });
    };

    uploadFiles = (params: UploadFilesParams): Promise<UploadFilesResponse> => {
        return this.httpClient.post('/upload-requisites/', params);
    };

    forwardDocument = (
        params: ForwardDocumentParams,
    ): Promise<ForwardDocumentResponse> => {
        return this.httpClient.post('/resend-document/', {
            email: params.email,
            document_id: params.documentId,
        });
    };

    getCurrentUser = (): Promise<GetCurrentUserResponse> => {
        const authToken = AuthTokenProvider.getToken();
        if (!authToken) {
            return Promise.reject();
        }

        return this.httpClient.get('/identification/');
    };

    getSummaryInfo = (): Promise<SummaryInfoResponse> => {
        return this.httpClient.get(`/summary/`);
    };

    sendFeedback = (formData: FormData): Promise<FeedbackResponse> => {
        return this.httpClient.post('/feedback/', formData);
    };

    getWarehouseRootCategories =
        (): Promise<GetWarehouseRootCategoriesResponse> => {
            if (this.mockIsEnabled) {
                return new Promise((res) =>
                    setTimeout(
                        () => res(mockGetWarehouseRootCategoriesResponse),
                        400,
                    ),
                );
            }

            return this.httpClient.get(`/categories/`);
        };

    getWarehouseSubcategories = (
        params: GetWarehouseSubcategoriesParams,
    ): Promise<GetWarehouseSubcategoriesResponse> => {
        // console.log('fetch subcategories', params);
        if (this.mockIsEnabled) {
            return new Promise((res) =>
                setTimeout(
                    () =>
                        res(
                            getMockGetWarehouseSubcategoriesResponse(
                                params.categoryId,
                            ),
                        ),
                    400,
                ),
            );
        }

        return this.httpClient.get(
            `/categories/subcategories/${params.categoryId}/`,
            {
                params: {
                    onTheWay: params.onTheWay,
                    inStock: params.inStock,
                },
            },
        );
    };

    getWarehouseProducts = (
        params: GetWarehouseProductsParams,
    ): Promise<GetWarehouseProductsResponse> => {
        // console.log('fetch products', params);
        if (this.mockIsEnabled) {
            return new Promise((res) =>
                setTimeout(
                    () =>
                        res(
                            getMockGetWarehouseProductsResponse({
                                categoryId: params.categoryId,
                                page: params.page,
                                sortType: `${params.sort?.type}${params.sort?.order}`,
                            }),
                        ),
                    400,
                ),
            );
        }

        return this.httpClient.get(
            `categories/products/${params.categoryId}/`,
            {
                params: {
                    page: params.page,
                    limit: params.limit,
                    onTheWay: params.onTheWay,
                    inStock: params.inStock,
                    sortType: params.sort?.type,
                    sortOrder: params.sort?.order,
                },
            },
        );
    };

    private setup = () => {
        this.httpClient.defaults.headers.common['X-CSRF-TOKEN'] =
            window.ENV?.UI?.token;

        const authToken = AuthTokenProvider.getToken();
        if (authToken) {
            this.httpClient.defaults.headers.common.Authorization = `Bearer ${authToken}`;
        }

        const httpClient = this.httpClient;
        this.httpClient.interceptors.response.use(
            function (response) {
                const token = response.data?.access_token;
                if (token) {
                    httpClient.defaults.headers.common.Authorization = `Bearer ${token}`;

                    const isDealersApiResponse = Boolean(
                        response.config.baseURL?.includes(
                            LK_DEALER_API_BASE_PATH,
                        ),
                    );
                    AuthTokenProvider.setToken({
                        token,
                        isDealer: isDealersApiResponse,
                    });
                }
                return response.data;
            },
            function (error: AxiosError) {
                return Promise.reject(error.response?.data || error);
            },
        );
    };
}
